<template>
  <div
    :class="[
      'admin-shell',
      `admin-shell--sidebar-${themeSettings.sidebar}`,
      `admin-shell--header-${themeSettings.header}`,
      `admin-shell--tabs-${themeSettings.tabs}`,
      { 'admin-shell--collapsed': sidebarCollapsed }
    ]"
    :style="themeStyle"
  >
    <header class="admin-shell__header">
      <div class="admin-shell__brand-panel">
        <RouterLink to="/admin" class="admin-shell__brand">
          {{ sidebarCollapsed && !isMobile ? t('admin.brandShort') : t('admin.brand') }}
        </RouterLink>
      </div>
      <div class="admin-shell__header-body">
        <div class="admin-shell__toolbar">
          <el-button
            text
            :icon="isMobile ? Menu : sidebarCollapsed ? Expand : Fold"
            :aria-label="isMobile ? t('admin.shell.openMenu') : sidebarCollapsed ? t('admin.nav.expandSidebar') : t('admin.nav.collapseSidebar')"
            @click="toggleSidebar"
          />
          <el-button
            text
            :icon="Refresh"
            :aria-label="t('admin.nav.refresh')"
            @click="navigation.refreshCurrent"
          />
          <nav class="admin-shell__breadcrumb" :aria-label="t('admin.shell.currentLocation')">
            <el-breadcrumb separator="/">
              <el-breadcrumb-item :to="{ path: '/admin/dashboard' }">
                {{ t('admin.nav.dashboard') }}
              </el-breadcrumb-item>
              <el-breadcrumb-item
                v-for="breadcrumb in currentBreadcrumbs"
                :key="breadcrumb.id"
              >
                {{ breadcrumb.title }}
              </el-breadcrumb-item>
            </el-breadcrumb>
          </nav>
        </div>
        <div class="admin-shell__account">
          <AdminLanguageSwitcher reload-on-change />
          <AdminTenantSwitch v-if="isPlatformAdmin && tenantCodeMode" />
          <AdminNoticeBell v-if="canReadNotices" />
          <el-tooltip :content="isFullscreen ? t('admin.shell.exitFullscreen') : t('admin.shell.enterFullscreen')" placement="bottom">
            <el-button
              text
              :icon="FullScreen"
              :aria-label="isFullscreen ? t('admin.shell.exitFullscreen') : t('admin.shell.enterFullscreen')"
              @click="toggleFullscreen"
            />
          </el-tooltip>
          <el-dropdown trigger="click" @command="handleAccountCommand">
            <button type="button" class="admin-shell__account-trigger" :aria-label="t('admin.shell.userMenu')">
              <el-avatar :size="32" :src="avatarUrl">{{ displayInitial }}</el-avatar>
              <span>{{ displayName }}</span>
              <el-icon><ArrowDown /></el-icon>
            </button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="password" :icon="Lock">{{ t('admin.shell.changePassword') }}</el-dropdown-item>
                <el-dropdown-item command="logout" :icon="SwitchButton" divided :disabled="loggingOut">
                  {{ t('admin.nav.logout') }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </header>

    <aside v-if="!isMobile" class="admin-shell__sidebar" aria-label="Admin 侧栏导航">
      <el-menu
        router
        :default-active="route.path"
        :collapse="sidebarCollapsed"
        :collapse-transition="false"
        :unique-opened="true"
        :background-color="menuBackgroundColor"
        :text-color="menuTextColor"
        :active-text-color="menuActiveTextColor"
        class="admin-shell__menu"
        data-testid="admin-sidebar-menu"
      >
        <AdminSidebarMenuItem
          v-for="item in visibleMenus"
          :key="item.id"
          :item="item"
          @navigate="closeMobileSidebar"
        />
      </el-menu>
    </aside>

    <el-drawer
      v-if="isMobile"
      v-model="mobileSidebarOpen"
      direction="ltr"
      size="230px"
      :with-header="false"
      append-to-body
      class="admin-shell__mobile-drawer"
      :style="mobileDrawerStyle"
    >
      <div class="admin-shell__mobile-brand">{{ t('admin.brand') }}</div>
      <el-menu
        router
        :default-active="route.path"
        :collapse="false"
        :collapse-transition="false"
        :unique-opened="true"
        :background-color="menuBackgroundColor"
        :text-color="menuTextColor"
        :active-text-color="menuActiveTextColor"
        class="admin-shell__menu admin-shell__mobile-menu"
        data-testid="admin-sidebar-menu"
        @select="closeMobileSidebar"
      >
        <AdminSidebarMenuItem
          v-for="item in visibleMenus"
          :key="item.id"
          :item="item"
          @navigate="closeMobileSidebar"
        />
      </el-menu>
    </el-drawer>

    <section class="admin-shell__workspace">
      <div class="admin-shell__tabs" data-testid="admin-tabs">
        <el-tabs
          :model-value="route.fullPath"
          :type="themeSettings.tabs === 'card' ? 'card' : undefined"
          @tab-click="selectTab"
          @tab-remove="closeTab"
        >
          <el-tab-pane
            v-for="tab in displayTabs"
            :key="tab.path"
            :name="tab.path"
            :label="tab.title"
            :closable="tab.closable"
          />
        </el-tabs>
        <el-dropdown trigger="click" placement="bottom-end" @command="handleTabsCommand">
          <el-button
            text
            :icon="ArrowDown"
            class="admin-shell__tabs-actions"
            :aria-label="t('admin.shell.tabActions')"
            :title="t('admin.shell.tabActions')"
          />
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="current" :disabled="route.path === '/admin/dashboard'">
                {{ t('admin.shell.closeCurrentTab') }}
              </el-dropdown-item>
              <el-dropdown-item command="other" :disabled="displayTabs.length <= 1">
                {{ t('admin.shell.closeOtherTabs') }}
              </el-dropdown-item>
              <el-dropdown-item command="all" :disabled="displayTabs.length <= 1" divided>
                {{ t('admin.shell.closeAllTabs') }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>

      <main class="admin-shell__content">
        <RouterView v-slot="{ Component, route: currentRoute }">
          <component
            :is="Component"
            :key="`${currentRoute.fullPath}:${navigation.refreshRevision}`"
          />
        </RouterView>
      </main>
    </section>
    <el-button
      class="admin-shell__settings-trigger"
      :icon="Setting"
      :aria-label="t('admin.shell.settings')"
      :title="t('admin.shell.settings')"
      @click="themeSettingsRef?.open()"
    />
    <AdminThemeSettings
      ref="themeSettingsRef"
      :model-value="themeSettings"
      @update:model-value="updateThemeSettings"
    />
    <AdminPasswordDialog ref="passwordDialogRef" :display-name="displayName" @changed="passwordChanged" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue'
import type { CSSProperties } from 'vue'
import { ElMessage } from 'element-plus'
import type { TabsPaneContext } from 'element-plus'
import {
  ArrowDown,
  Expand,
  Fold,
  FullScreen,
  Lock,
  Menu,
  Refresh,
  Setting,
  SwitchButton
} from '@element-plus/icons-vue'
import { useFullscreen, useMediaQuery } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import defaultAvatar from '@/shared/assets/avatar.jpg'
import AdminSidebarMenuItem from '@/apps/admin/components/AdminSidebarMenuItem.vue'
import AdminLanguageSwitcher from '@/apps/admin/components/AdminLanguageSwitcher.vue'
import AdminNoticeBell from '@/apps/admin/components/AdminNoticeBell.vue'
import AdminPasswordDialog from '@/apps/admin/components/AdminPasswordDialog.vue'
import AdminThemeSettings from '@/apps/admin/components/AdminThemeSettings.vue'
import AdminTenantSwitch from '@/apps/admin/components/AdminTenantSwitch.vue'
import { isAdminTenantCodeMode } from '@/apps/admin/config/tenant-mode'
import { resolveAdminMenuBreadcrumbs } from '@/apps/admin/utils/menu-breadcrumb'
import { getVisibleAdminMenus } from '@/apps/admin/utils/menu-presentation'
import {
  readAdminSidebarCollapsed,
  writeAdminSidebarCollapsed
} from '@/apps/admin/utils/sidebar-preference'
import {
  readAdminTheme,
  writeAdminTheme,
  type AdminThemeSettings as AdminThemeSettingsValue
} from '@/apps/admin/utils/theme-settings'
import { useAdminSessionStore } from '@/stores/admin/session'
import { useAdminApplicationStore } from '@/stores/admin/application'
import { useAdminNavigationStore } from '@/stores/admin/navigation'
import { useAdminPermissions } from '@/apps/admin/utils/permissions'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const adminSession = useAdminSessionStore()
const adminApplication = useAdminApplicationStore()
const navigation = useAdminNavigationStore()
const { hasPermission } = useAdminPermissions()
const tenantCodeMode = isAdminTenantCodeMode()
const { isFullscreen, toggle: toggleFullscreen } = useFullscreen()
const isMobile = useMediaQuery('(max-width: 768px)')
const loggingOut = ref(false)
const sidebarCollapsed = ref(readAdminSidebarCollapsed())
const mobileSidebarOpen = ref(false)
const passwordDialogRef = ref<InstanceType<typeof AdminPasswordDialog>>()
const themeSettingsRef = ref<InstanceType<typeof AdminThemeSettings>>()
const themeSettings = ref<AdminThemeSettingsValue>(readAdminTheme())
const canReadNotices = computed(() => hasPermission('sys:notice:all'))
const isPlatformAdmin = computed(() => {
  return adminSession.user?.superAdmin === 1 && String(adminSession.user?.tenantCode || '') === '10000'
})

const displayName = computed(() => {
  return adminSession.user?.realName || adminSession.user?.username || t('admin.shell.administrator')
})
const displayInitial = computed(() => displayName.value.slice(0, 1).toUpperCase())
const avatarUrl = computed(() => adminSession.user?.headUrl?.trim() || defaultAvatar)
const menuBackgroundColor = computed(() => themeSettings.value.sidebar === 'dark' ? '#263238' : '#ffffff')
const menuTextColor = computed(() => themeSettings.value.sidebar === 'dark' ? '#cfd8dc' : '#4b5563')
const menuActiveTextColor = computed(() => themeSettings.value.sidebar === 'dark' ? '#ffffff' : themeSettings.value.primaryColor)
const mobileDrawerStyle = computed<CSSProperties>(() => ({
  '--admin-header-height': '50px',
  '--admin-sidebar-width': '230px',
  '--admin-mobile-sidebar-background': menuBackgroundColor.value,
  '--admin-mobile-sidebar-text': menuTextColor.value
}))
const themeStyle = computed<CSSProperties>(() => {
  const headerBackground = themeSettings.value.header === 'primary'
    ? themeSettings.value.primaryColor
    : themeSettings.value.header === 'dark' ? '#263238' : '#ffffff'
  const headerText = themeSettings.value.header === 'light' ? '#4b5563' : '#ffffff'

  return {
    '--admin-color-primary': themeSettings.value.primaryColor,
    '--admin-header-background': headerBackground,
    '--admin-header-text': headerText,
    '--admin-sidebar-background': menuBackgroundColor.value,
    '--admin-sidebar-text': menuTextColor.value,
    '--el-color-primary': themeSettings.value.primaryColor
  }
})
const visibleMenus = computed(() => {
  return getVisibleAdminMenus(adminApplication.menus)
})
const displayTabs = computed(() => {
  return navigation.tabs.map(tab => ({
    ...tab,
    title: tab.titleKey ? t(tab.titleKey) : tab.title
  }))
})

const dynamicPageTitle = computed(() => {
  const value = route.query._mt
  return typeof value === 'string' ? value.trim().slice(0, 200) : ''
})
const menuBreadcrumbs = computed(() => resolveAdminMenuBreadcrumbs(
  adminApplication.menus,
  route.path,
  t,
  dynamicPageTitle.value
))
const currentPageTitle = computed(() => {
  return menuBreadcrumbs.value.at(-1)?.title || (route.path === '/admin/dashboard'
    ? t('admin.nav.dashboard')
    : dynamicPageTitle.value || (
      typeof route.meta.titleKey === 'string'
        ? t(route.meta.titleKey)
        : String(route.meta.title || t('admin.nav.page'))
    ))
})
const currentBreadcrumbs = computed(() => {
  if (route.path === '/admin/dashboard') return []
  if (menuBreadcrumbs.value.length) return menuBreadcrumbs.value
  return [{ id: String(route.name || route.path), title: currentPageTitle.value }]
})

const updateThemeSettings = (value: AdminThemeSettingsValue): void => {
  themeSettings.value = value
  writeAdminTheme(value)
}

const handleAccountCommand = (command: string): void => {
  if (command === 'password') {
    passwordDialogRef.value?.open()
  } else if (command === 'logout') {
    void logout()
  }
}

const toggleSidebar = (): void => {
  if (isMobile.value) {
    mobileSidebarOpen.value = true
  } else {
    sidebarCollapsed.value = !sidebarCollapsed.value
    writeAdminSidebarCollapsed(sidebarCollapsed.value)
  }
}

const closeMobileSidebar = (): void => {
  mobileSidebarOpen.value = false
}

watchEffect(() => {
  if (typeof document !== 'undefined') {
    document.title = t('admin.brand')
  }
})

watch(
  () => route.fullPath,
  fullPath => {
    closeMobileSidebar()
    if (!fullPath.startsWith('/admin/') || route.name === 'admin-initialization-error') {
      return
    }
    navigation.openTab({
      path: fullPath,
      titleKey: !dynamicPageTitle.value && !menuBreadcrumbs.value.length &&
        typeof route.meta.titleKey === 'string'
        ? route.meta.titleKey
        : undefined,
      title: currentPageTitle.value
    })
  },
  { immediate: true }
)

watch(isMobile, mobile => {
  if (!mobile) closeMobileSidebar()
})

const selectTab = (pane: TabsPaneContext): void => {
  if (typeof pane.paneName === 'string' && pane.paneName !== route.fullPath) {
    void router.push(pane.paneName)
  }
}

const closeTab = (name: string | number): void => {
  if (typeof name !== 'string') return
  const fallback = navigation.closeTab(name, route.fullPath)
  if (fallback) void router.push(fallback)
}

const handleTabsCommand = (command: string): void => {
  let destination: string | null = null
  if (command === 'current') {
    destination = navigation.closeTab(route.fullPath, route.fullPath)
  } else if (command === 'other') {
    destination = navigation.closeOtherTabs(route.fullPath)
  } else if (command === 'all') {
    destination = navigation.closeAllTabs()
  }

  if (destination && destination !== route.fullPath) {
    void router.push(destination)
  }
}

const logout = async (): Promise<void> => {
  if (loggingOut.value) return

  loggingOut.value = true
  try {
    await adminSession.logout()
    ElMessage.success(t('admin.notice.logoutSuccess'))
  } catch {
    ElMessage.warning(t('admin.notice.logoutWarning'))
  } finally {
    adminApplication.reset()
    navigation.reset()
    loggingOut.value = false
    await router.replace({ name: 'admin-login' })
  }
}

const passwordChanged = async (): Promise<void> => {
  await adminSession.logout().catch(() => adminSession.clearSession())
  adminApplication.reset()
  navigation.reset()
  await router.replace({ name: 'admin-login' })
}
</script>

<style scoped>
.admin-shell {
  --admin-sidebar-current-width: var(--admin-sidebar-width);

  display: grid;
  grid-template: var(--admin-header-height) minmax(0, 1fr) / var(--admin-sidebar-current-width) minmax(0, 1fr);
  height: 100vh;
  min-height: 100vh;
  overflow: hidden;
  transition: grid-template-columns 0.2s ease;
}

.admin-shell--collapsed {
  --admin-sidebar-current-width: 64px;
}

.admin-shell__header {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  border-bottom: 1px solid color-mix(in srgb, var(--admin-header-text) 18%, transparent);
  background: var(--admin-header-background);
  color: var(--admin-header-text);
  box-shadow: 0 1px 4px rgb(15 23 42 / 10%);
  z-index: 4;
}

.admin-shell__brand-panel {
  display: flex;
  width: var(--admin-sidebar-current-width);
  height: 100%;
  flex: 0 0 var(--admin-sidebar-current-width);
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  background: transparent;
  transition: width 0.2s ease, flex-basis 0.2s ease;
}

.admin-shell__brand {
  overflow: hidden;
  color: inherit;
  font-size: 20px;
  font-weight: 700;
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-shell__header-body {
  display: flex;
  min-width: 0;
  flex: 1;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px 0 8px;
}

.admin-shell__toolbar {
  display: flex;
  min-width: 0;
  align-items: center;
}

.admin-shell__header :deep(.el-button.is-text) {
  color: inherit;
  font-size: 18px;
}

.admin-shell__header :deep(.el-button.is-text:hover) {
  background: rgb(255 255 255 / 14%);
  color: inherit;
}

.admin-shell--header-light .admin-shell__header :deep(.el-button.is-text:hover) {
  background: rgb(15 23 42 / 6%);
}

.admin-shell__breadcrumb {
  overflow: hidden;
  margin-left: 8px;
  font-size: 14px;
  opacity: 0.86;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.admin-shell__breadcrumb :deep(.el-breadcrumb) {
  flex-wrap: nowrap;
}

.admin-shell__breadcrumb :deep(.el-breadcrumb__item) {
  float: none;
}

.admin-shell__breadcrumb :deep(.el-breadcrumb__inner),
.admin-shell__breadcrumb :deep(.el-breadcrumb__separator) {
  color: inherit;
  font-weight: 400;
}

.admin-shell__breadcrumb :deep(.el-breadcrumb__inner.is-link:hover) {
  color: inherit;
  opacity: 0.82;
}

.admin-shell__account,
.admin-shell__tabs {
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-shell__account {
  flex: 0 0 auto;
  color: inherit;
}

.admin-shell__account-trigger {
  display: flex;
  height: 42px;
  padding: 0 4px;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.admin-shell__account-trigger:hover {
  opacity: 0.88;
}

.admin-shell__account-trigger .el-icon {
  font-size: 12px;
}

.admin-shell__account-trigger :deep(.el-avatar) {
  border: 2px solid rgb(255 255 255 / 55%);
  background: color-mix(in srgb, var(--admin-color-primary) 82%, #263238);
  color: #fff;
}

.admin-shell--header-light .admin-shell__account-trigger :deep(.el-avatar) {
  border-color: var(--admin-color-border);
}

.admin-shell__sidebar {
  min-height: 0;
  overflow: hidden;
  border-right: 1px solid color-mix(in srgb, var(--admin-sidebar-text) 15%, transparent);
  background: var(--admin-sidebar-background);
}

.admin-shell__menu {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  border-right: 0;
}

.admin-shell__menu:not(.el-menu--collapse) {
  width: var(--admin-sidebar-width);
}

.admin-shell__mobile-brand {
  display: flex;
  height: var(--admin-header-height);
  align-items: center;
  padding: 0 20px;
  background: var(--admin-mobile-sidebar-background);
  color: var(--admin-mobile-sidebar-text);
  font-size: 18px;
  font-weight: 700;
}

.admin-shell__mobile-menu {
  height: calc(100vh - var(--admin-header-height));
}

:global(.admin-shell__mobile-drawer) {
  background: var(--admin-mobile-sidebar-background);
}

:global(.admin-shell__mobile-drawer .el-drawer__body) {
  padding: 0;
  overflow: hidden;
  background: var(--admin-mobile-sidebar-background);
}

.admin-shell__menu :deep(.el-menu-item.is-active) {
  background: rgb(255 255 255 / 12%);
}

.admin-shell--sidebar-light .admin-shell__menu :deep(.el-menu-item.is-active) {
  background: color-mix(in srgb, var(--admin-color-primary) 10%, #fff);
}

.admin-shell__menu :deep(.el-menu-item:hover),
.admin-shell__menu :deep(.el-sub-menu__title:hover) {
  background: rgb(255 255 255 / 8%);
}

.admin-shell--sidebar-light .admin-shell__menu :deep(.el-menu-item:hover),
.admin-shell--sidebar-light .admin-shell__menu :deep(.el-sub-menu__title:hover) {
  background: #f3f4f6;
}

.admin-shell__workspace {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  background: var(--admin-color-background);
}

.admin-shell__tabs {
  justify-content: space-between;
  min-height: var(--admin-tabs-height);
  padding: 0 18px;
  border-bottom: 1px solid var(--admin-color-border);
  background: var(--admin-color-surface);
  box-shadow: 0 2px 7px rgb(15 23 42 / 5%);
}

.admin-shell__tabs :deep(.el-tabs) {
  min-width: 0;
  flex: 1;
}

.admin-shell__tabs-actions {
  flex: 0 0 auto;
  color: var(--admin-color-text-muted);
}

.admin-shell__tabs :deep(.el-tabs__header) {
  margin: 0;
}

.admin-shell__tabs :deep(.el-tabs__nav-wrap::after) {
  height: 0;
}

.admin-shell--tabs-default .admin-shell__tabs :deep(.el-tabs__item) {
  height: var(--admin-tabs-height);
  padding: 0 18px;
}

.admin-shell--tabs-default .admin-shell__tabs :deep(.el-tabs__active-bar) {
  height: 2px;
  background: var(--admin-color-primary);
}

.admin-shell__content {
  min-height: 0;
  flex: 1;
  overflow: auto;
}

.admin-shell__settings-trigger {
  position: fixed;
  top: 25%;
  right: 0;
  width: 44px;
  height: 48px;
  border: 0;
  border-radius: 6px 0 0 6px;
  background: var(--admin-color-primary);
  color: #fff;
  box-shadow: 0 3px 10px rgb(15 23 42 / 18%);
  z-index: 20;
}

.admin-shell__settings-trigger:hover,
.admin-shell__settings-trigger:focus {
  background: color-mix(in srgb, var(--admin-color-primary) 88%, #000);
  color: #fff;
}

@media (max-width: 768px) {
  .admin-shell {
    grid-template: var(--admin-header-height) minmax(0, 1fr) / minmax(0, 1fr);
  }

  .admin-shell__header {
    grid-column: 1;
  }

  .admin-shell__brand-panel {
    width: auto;
    min-width: 0;
    flex: 0 1 auto;
    padding: 0 10px;
  }

  .admin-shell__brand {
    max-width: 132px;
    font-size: 16px;
  }

  .admin-shell__account > span {
    display: none;
  }

  .admin-shell__breadcrumb,
  .admin-shell__account-trigger > span {
    display: none;
  }
}
</style>
